import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppointmentModal from '../components/AppointmentModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { apiService } from '../services/api';
import {
  buildAppointmentPayload,
  mapServerStatusToUIStatus,
  mapUIStatusToServerStatus,
  normalizeAppointmentForUI,
} from './appointmentsMapper';
import { getCalendarDays } from './appointmentViewUtils';
import './Appointments.css';

const Appointments = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const statuses = ['all', 'Scheduled', 'Completed', 'Cancelled', 'Rescheduled'];

  useEffect(() => {
    const loadAppointments = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.appointments.getAll({ limit: 100 });
        const serverAppointments = Array.isArray(response?.data?.data) ? response.data.data : [];
        setAppointments(serverAppointments.map(normalizeAppointmentForUI));
      } catch (err) {
        console.error('Failed to load appointments:', err);
        setError('Unable to load appointments right now.');
      } finally {
        setLoading(false);
      }
    };

    loadAppointments();
  }, []);

  const filteredAppointments = appointments.filter((appointment) => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const matchesSearch = !normalizedQuery || [
      appointment.title,
      appointment.customerName,
      appointment.customerEmail,
      appointment.customerPhone,
    ].some((value) => String(value || '').toLowerCase().includes(normalizedQuery));

    const matchesStatus = selectedStatus === 'all' || appointment.status === selectedStatus;
    const matchesDate = selectedDate === 'all' || appointment.date === selectedDate;

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleAddAppointment = () => {
    setEditingAppointment(null);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleDeleteAppointment = (appointment) => {
    setAppointmentToDelete(appointment);
    setIsDeleteModalOpen(true);
  };

  const handleSaveAppointment = async (formData) => {
    try {
      const payload = buildAppointmentPayload(formData);

      if (editingAppointment) {
        const response = await apiService.appointments.update(editingAppointment._id || editingAppointment.id, payload);
        const updatedAppointment = normalizeAppointmentForUI(response?.data?.data || response?.data || {});
        setAppointments((prev) => prev.map((item) => (item.id === updatedAppointment.id ? updatedAppointment : item)));
      } else {
        const response = await apiService.appointments.create(payload);
        const createdAppointment = normalizeAppointmentForUI(response?.data?.data || response?.data || {});
        setAppointments((prev) => [createdAppointment, ...prev]);
      }
    } catch (err) {
      console.error('Failed to save appointment:', err);
      setError('Unable to save the appointment.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!appointmentToDelete) return;

    try {
      await apiService.appointments.delete(appointmentToDelete._id || appointmentToDelete.id);
      setAppointments((prev) => prev.filter((item) => (item._id || item.id) !== (appointmentToDelete._id || appointmentToDelete.id)));
      setIsDeleteModalOpen(false);
      setAppointmentToDelete(null);
    } catch (err) {
      console.error('Failed to delete appointment:', err);
      setError('Unable to delete the appointment.');
    }
  };

  const handleStatusChange = async (appointment, newStatus) => {
    const nextStatus = mapUIStatusToServerStatus(newStatus);
    try {
      const response = await apiService.appointments.update(appointment._id || appointment.id, {
        status: nextStatus,
      });
      const updatedAppointment = normalizeAppointmentForUI(response?.data?.data || response?.data || {});
      setAppointments((prev) => prev.map((item) => (item.id === updatedAppointment.id ? updatedAppointment : item)));
    } catch (err) {
      console.error('Failed to update appointment status:', err);
      setError('Unable to update appointment status.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '—';
    const normalizedTime = String(timeString).includes(':') ? timeString : `${timeString}:00`;
    const [hours, minutes] = normalizedTime.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Scheduled: { color: '#3b82f6', bg: '#dbeafe' },
      Completed: { color: '#10b981', bg: '#d1fae5' },
      Cancelled: { color: '#ef4444', bg: '#fee2e2' },
      Rescheduled: { color: '#f59e0b', bg: '#fef3c7' }
    };

    const config = statusConfig[status] || statusConfig.Scheduled;
    return (
      <span
        className="status-badge"
        style={{
          background: config.bg,
          color: config.color
        }}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return <span className="type-badge">{type}</span>;
  };

  const getUpcomingAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments
      .filter((appointment) => appointment.status === 'Scheduled' && appointment.date >= today)
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.time.localeCompare(b.time);
      })
      .slice(0, 5);
  };

  const upcomingAppointments = getUpcomingAppointments();
  const calendarDays = getCalendarDays(filteredAppointments, calendarMonth);

  const changeCalendarMonth = (direction) => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
  };

  return (
    <div className="appointments-container">
      <div className="appointments-header">
        <div className="header-content">
          <h1>Appointments</h1>
          <p>Manage your scheduled appointments and meetings</p>
        </div>
        <button className="btn-add-appointment" onClick={handleAddAppointment}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Schedule Appointment
        </button>
      </div>

      {error && <div className="error-state">{error}</div>}

      {/* Upcoming Appointments Quick View */}
      {upcomingAppointments.length > 0 && (
        <div className="upcoming-section">
          <h3>Upcoming Appointments</h3>
          <div className="upcoming-list">
            {upcomingAppointments.map(appointment => (
              <div key={appointment.id} className="upcoming-item">
                <div className="upcoming-date">
                  <div className="date-day">{new Date(appointment.date).getDate()}</div>
                  <div className="date-month">{new Date(appointment.date).toLocaleDateString('en-US', { month: 'short' })}</div>
                </div>
                <div className="upcoming-info">
                  <div className="upcoming-title">{appointment.title}</div>
                  <div className="upcoming-customer">{appointment.customerName}</div>
                  <div className="upcoming-time">{formatTime(appointment.time)} • {appointment.duration} min</div>
                </div>
                <div className="upcoming-actions">
                  <button
                    className="action-btn-small"
                    onClick={() => handleEditAppointment(appointment)}
                    title="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="appointments-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search appointments by title, customer name, or contact..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="filter-select date-input"
          />
          {selectedDate !== 'all' && (
            <button
              className="btn-clear-filter"
              onClick={() => setSelectedDate('all')}
              title="Clear date filter"
            >
              ✕
            </button>
          )}
        </div>

        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
          </button>
          <button
            className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewMode('calendar')}
            title="Calendar View"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </button>
        </div>

        <div className="appointments-stats">
          <span className="stat-item">
            Total: <strong>{appointments.length}</strong>
          </span>
          <span className="stat-item">
            Scheduled: <strong>{appointments.filter((appointment) => appointment.status === 'Scheduled').length}</strong>
          </span>
          <span className="stat-item">
            Today: <strong>{appointments.filter((appointment) => appointment.date === new Date().toISOString().split('T')[0] && appointment.status === 'Scheduled').length}</strong>
          </span>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="appointments-list">
          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <h3>Loading appointments...</h3>
              <p>Please wait while we fetch your appointment data.</p>
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3>No appointments found</h3>
              <p>Try adjusting your search or filter criteria, or schedule a new appointment</p>
              <button className="btn-add-first" onClick={handleAddAppointment}>
                Schedule Appointment
              </button>
            </div>
          ) : (
            filteredAppointments.map(appointment => (
              <div key={appointment.id} className="appointment-card">
                <div className="appointment-header">
                  <div className="appointment-title-section">
                    <h3 className="appointment-title">{appointment.title}</h3>
                    {getTypeBadge(appointment.type)}
                  </div>
                  <div className="appointment-status-group">
                    {getStatusBadge(appointment.status)}
                    {appointment.reminderSent && (
                      <span className="reminder-badge" title="Reminder sent">
                        🔔
                      </span>
                    )}
                  </div>
                </div>

                <div className="appointment-details">
                  <div className="detail-row">
                    <div className="detail-pill">
                      <span className="detail-icon">👤</span>
                      <div className="detail-content">
                        <span className="detail-label">Customer</span>
                        <span className="detail-value">{appointment.customerName}</span>
                      </div>
                    </div>
                    <div className="detail-pill">
                      <span className="detail-icon">📞</span>
                      <div className="detail-content">
                        <span className="detail-label">Phone</span>
                        <span className="detail-value">{appointment.customerPhone}</span>
                      </div>
                    </div>
                    <div className="detail-pill">
                      <span className="detail-icon">📅</span>
                      <div className="detail-content">
                        <span className="detail-label">Date & Time</span>
                        <span className="detail-value">{formatDate(appointment.date)} • {formatTime(appointment.time)}</span>
                      </div>
                    </div>
                    <div className="detail-pill">
                      <span className="detail-icon">⏱️</span>
                      <div className="detail-content">
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{appointment.duration} mins</span>
                      </div>
                    </div>
                  </div>

                  {(appointment.notes || appointment.cancellationReason) && (
                    <div className="notes-block">
                      {appointment.notes && (
                        <div className="appointment-notes">
                          <span className="notes-label">Notes:</span>
                          <p>{appointment.notes}</p>
                        </div>
                      )}

                      {appointment.cancellationReason && (
                        <div className="cancellation-reason">
                          <span className="reason-label">Cancellation Reason:</span>
                          <p>{appointment.cancellationReason}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="appointment-actions">
                  {appointment.status === 'Scheduled' && (
                    <>
                      <button
                        className="action-btn complete-btn"
                        onClick={() => handleStatusChange(appointment, 'Completed')}
                        title="Mark as Completed"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Complete
                      </button>
                      <button
                        className="action-btn cancel-btn"
                        onClick={() => handleStatusChange(appointment, 'Cancelled')}
                        title="Cancel Appointment"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        Cancel
                      </button>
                    </>
                  )}
                  <button
                    className="action-btn contact-btn"
                    onClick={() => navigate(`/conversations/${appointment.id || appointment._id}`)}
                    title="Contact Customer"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Contact
                  </button>
                  <button
                    className="action-btn edit-btn"
                    onClick={() => handleEditAppointment(appointment)}
                    title="Edit Appointment"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteAppointment(appointment)}
                    title="Delete Appointment"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="calendar-view">
          <div className="calendar-header">
            <button className="calendar-nav" onClick={() => changeCalendarMonth(-1)}>{'<'}</button>
            <h3>{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
            <button className="calendar-nav" onClick={() => changeCalendarMonth(1)}>{'>'}</button>
          </div>

          <div className="calendar-grid">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="calendar-weekday">{day}</div>
            ))}
            {calendarDays.map((day, index) => (
              <div key={`${day.dateKey || 'empty'}-${index}`} className={`calendar-day ${day.isCurrentMonth ? '' : 'muted'}`}>
                <div className="calendar-day-number">{day.date ? day.date.getDate() : ''}</div>
                <div className="calendar-day-events">
                  {day.appointments.slice(0, 2).map((appointment) => (
                    <div key={appointment.id} className="calendar-event">
                      <span>{appointment.time}</span>
                      <strong>{appointment.title}</strong>
                    </div>
                  ))}
                  {day.appointments.length > 2 && <div className="calendar-more">+{day.appointments.length - 2} more</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAppointment(null);
        }}
        onSave={handleSaveAppointment}
        appointment={editingAppointment}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAppointmentToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={appointmentToDelete?.title || ''}
        type="appointment"
      />
    </div>
  );
};

export default Appointments;



